import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { useGetBusinessIfExists } from '@marketing-milk/frontend'
import { useSprintPacket } from 'hooks/api/useSprintPackets'
import { CommentAnchor, CommentThread } from 'components/comments/CommentThread'
import { AllCommentsDrawer } from 'components/comments/AllCommentsDrawer'
import { anchorSectionKey } from 'components/comments/groupCommentsByAnchor'
import { countUnresolvedTotal } from 'components/comments/commentCounts'
import { readCommentDeepLink } from 'components/comments/commentDeepLink'
import { useCommentAnchors } from './useCommentAnchors'
import { UnresolvedCommentsProvider } from './UnresolvedCommentsContext'
import { SECTIONS, SectionDef } from './sections'
import { dark, type } from '../tokens'
import { PresentCover } from './present/PresentCover'
import { PresentProgress } from './present/PresentProgress'
import { PresentPerformance } from './present/sections/PresentPerformance'
import { PresentNarrative } from './present/sections/PresentNarrative'
import { PresentClientSentiment } from './present/sections/PresentClientSentiment'
import { PresentAudienceSection } from './present/sections/PresentAudienceSection'
import { PresentCreativeSection } from './present/sections/PresentCreativeSection'
import { PresentNavLockContext } from './present/PresentNavLock'
import { creativeWizardUrl } from '../../creatives/creativeUrl'
import { audiencesUrl } from '../../audiences/audienceUrl'

interface Props {
  businessID: number
  packetID: number
}

/** Compile-time exhaustiveness guard for the section-kind switch. */
const assertNever = (x: never): never => {
  throw new Error(`Unhandled section kind: ${x}`)
}

type Page = { kind: 'cover' } | { kind: 'section'; section: SectionDef }

const PAGES: Page[] = [
  { kind: 'cover' },
  ...SECTIONS.map(s => ({ kind: 'section' as const, section: s })),
]

const PAGE_LABELS = ['Cover', ...SECTIONS.map(s => s.label)]

export function PacketPresentBody({ businessID, packetID }: Props) {
  const { packet, isLoading, isError, refetch } = useSprintPacket(packetID)
  const business = useGetBusinessIfExists()
  const history = useHistory()
  const { anchor, open, close, setAnchor, setOpen, setRoot } = useCommentAnchors()
  const [pageIndex, setPageIndex] = useState(0)
  const [navLocked, setNavLocked] = useState(false)
  const [allCommentsOpen, setAllCommentsOpen] = useState(false)
  const [threadTargetCommentId, setThreadTargetCommentId] = useState<number | null>(null)
  const handledDeepLinkRef = useRef(false)

  // Jump the deck to an anchor's slide and open its thread. `anchorSectionKey`
  // maps every anchor (incl. creative -> 'creative', audience ->
  // 'audience-framework', performance-metric -> 'performance-review') to a real
  // section slide, so entity anchors land explicitly. Shared by the Slack
  // deep-link effect and the "All comments" list. `commentId` drives
  // CommentThread's in-app highlight; omit it for deep-links (it reads ?commentId=).
  const openAnchor = useCallback(
    (target: CommentAnchor, commentId?: number | null) => {
      const key = anchorSectionKey(target.type, target.id)
      const idx = PAGES.findIndex(p => p.kind === 'section' && p.section.key === key)
      if (idx >= 0) setPageIndex(idx)
      setThreadTargetCommentId(commentId ?? null)
      setAnchor(target)
      setOpen(true)
    },
    [setAnchor, setOpen]
  )

  // Slack comment deep-link (`?commentId=&anchorType=&anchorId=`): jump the deck
  // to the matching slide, then open the thread. The ref latch keeps this run-once
  // so a packet refetch can't yank the viewer back mid-deck.
  useEffect(() => {
    if (!packet || handledDeepLinkRef.current) return
    const link = readCommentDeepLink()
    if (!link) return
    handledDeepLinkRef.current = true
    openAnchor({ type: link.anchorType, id: link.anchorId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packet])

  const closeThread = useCallback(() => {
    close()
    setThreadTargetCommentId(null)
  }, [close])

  const onSelectComment = useCallback(
    (target: CommentAnchor, commentId: number) => {
      setAllCommentsOpen(false)
      openAnchor(target, commentId)
    },
    [openAnchor]
  )

  const unresolvedTotal = useMemo(
    () => countUnresolvedTotal(packet?.comments ?? []),
    [packet?.comments]
  )

  const exitToEditor = useMemo(
    () => (index: number) => {
      const page = PAGES[index]
      const hash = page.kind === 'section' ? `#${page.section.key}` : ''
      history.push(`/${businessID}/sprint-packets/${packetID}/edit${hash}`)
    },
    [history, businessID, packetID]
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack keys while the AE is typing in the live comment drawer, an
      // open in-slide modal (e.g. creative approval), or the "All comments" list —
      // arrow keys must move the text cursor/list and Esc must close the open
      // surface, not discard work or move the deck.
      if (open || navLocked || allCommentsOpen) return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      if (e.key === 'ArrowRight') setPageIndex(i => Math.min(i + 1, PAGES.length - 1))
      else if (e.key === 'ArrowLeft') setPageIndex(i => Math.max(i - 1, 0))
      else if (e.key === 'Escape') exitToEditor(pageIndex)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pageIndex, exitToEditor, open, navLocked, allCommentsOpen])

  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: dark.bg,
    color: dark.text,
    fontFamily: type.family.sans,
  }

  if (isLoading) {
    return (
      <div
        style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress size={28} sx={{ color: dark.accent }} />
      </div>
    )
  }

  if (isError || !packet) {
    return (
      <div
        style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: type.family.serif,
              fontSize: type.size.h1,
              fontWeight: type.weight.regular,
            }}
          >
            Couldn't load this sprint packet
          </h2>
          <button
            type="button"
            onClick={() => refetch()}
            style={{
              alignSelf: 'center',
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${dark.hairline}`,
              background: dark.surface,
              color: dark.text,
              fontFamily: type.family.mono,
              fontSize: type.size.eyebrow,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const renderSection = (s: SectionDef) => {
    switch (s.kind) {
      case 'performance':
        return <PresentPerformance palette={dark} packet={packet} />
      case 'narrative':
        return (
          <PresentNarrative
            palette={dark}
            sectionKey={
              s.key as Exclude<
                typeof s.key,
                'performance-review' | 'client-sentiment' | 'audience-framework' | 'creative'
              >
            }
            label={s.label}
            packetID={packet.id}
            narrative={packet.narrative ?? []}
          />
        )
      case 'client-sentiment':
        return (
          <PresentClientSentiment
            palette={dark}
            packetID={packet.id}
            sentiment={packet.clientSentiment ?? null}
          />
        )
      case 'audience':
        return (
          <PresentAudienceSection
            palette={dark}
            packetID={packet.id}
            audiences={packet.audiences}
            audienceSelection={packet.audienceSelection ?? null}
          />
        )
      case 'creative':
        return (
          <PresentCreativeSection
            palette={dark}
            businessID={businessID}
            packetID={packet.id}
            sprintID={packet.sprintID}
          />
        )
      default:
        // Exhaustive over SectionKind today; a new kind fails the type-check here.
        return assertNever(s.kind)
    }
  }

  return (
    <UnresolvedCommentsProvider comments={packet.comments}>
      <PresentNavLockContext.Provider value={setNavLocked}>
        <style>{`
        .present-slide-scroll::-webkit-scrollbar { width: 8px; }
        .present-slide-scroll::-webkit-scrollbar-track { background: transparent; }
        .present-slide-scroll::-webkit-scrollbar-thumb { background: ${dark.hairline}; border-radius: 4px; }
        .present-slide-scroll::-webkit-scrollbar-thumb:hover { background: ${dark.textFaint}; }
      `}</style>
        <div ref={setRoot} style={baseStyle}>
          <button
            type="button"
            onClick={() => setAllCommentsOpen(true)}
            style={{
              position: 'absolute',
              top: 20,
              left: 24,
              zIndex: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${dark.hairline}`,
              background: dark.surface,
              color: dark.textMuted,
              fontFamily: type.family.mono,
              fontSize: type.size.eyebrow,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            All comments
            {unresolvedTotal > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 16,
                  height: 16,
                  padding: '0 5px',
                  borderRadius: 999,
                  background: dark.accent,
                  color: dark.accentInk,
                  fontSize: 10,
                  fontWeight: type.weight.semibold,
                  lineHeight: 1,
                }}
              >
                {unresolvedTotal}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => exitToEditor(pageIndex)}
            style={{
              position: 'absolute',
              top: 20,
              right: 24,
              zIndex: 2,
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${dark.hairline}`,
              background: dark.surface,
              color: dark.textMuted,
              fontFamily: type.family.mono,
              fontSize: type.size.eyebrow,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Esc · Editor
          </button>

          {PAGES.map((page, i) => {
            const isActive = i === pageIndex
            return (
              <div
                key={i}
                aria-hidden={!isActive}
                style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: isActive ? 1 : 0,
                  pointerEvents: isActive ? 'auto' : 'none',
                  // `visibility: hidden` pulls inactive slides out of the tab order and
                  // the accessibility tree (their comment triggers would otherwise stay
                  // focusable). Delay the flip on the way out so the 200ms fade still plays.
                  visibility: isActive ? 'visible' : 'hidden',
                  transition: isActive
                    ? 'opacity 200ms ease'
                    : 'opacity 200ms ease, visibility 0s linear 200ms',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <div
                  className="present-slide-scroll"
                  style={{
                    width: '100%',
                    maxWidth: 1024,
                    height: '100%',
                    overflowY: 'auto',
                    padding: '64px 48px 88px',
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${dark.hairline} transparent`,
                  }}
                >
                  {page.kind === 'cover' ? (
                    <PresentCover
                      palette={dark}
                      packet={packet}
                      businessName={business?.businessName}
                      phase={business?.phase}
                    />
                  ) : (
                    renderSection(page.section)
                  )}
                </div>
              </div>
            )
          })}

          <PresentProgress
            palette={dark}
            count={PAGES.length}
            current={pageIndex}
            onJump={setPageIndex}
            labels={PAGE_LABELS}
          />
        </div>

        <AllCommentsDrawer
          open={allCommentsOpen}
          onClose={() => setAllCommentsOpen(false)}
          palette={dark}
          comments={packet.comments}
          audiences={packet.audiences}
          onSelectComment={onSelectComment}
        />

        <CommentThread
          open={open}
          onClose={closeThread}
          businessID={businessID}
          anchor={anchor}
          targetCommentId={threadTargetCommentId}
          // Refetch the packet on a comment mutation so hydrated comments (the badge
          // source) refresh and slide-header unresolved badges clear on resolve.
          onCommentsChanged={refetch}
          notificationContext={{
            header: `Sprint Packet — ${packet.sprint?.name ?? `#${packet.sprintID}`}`,
            pageUrlBase: window.location.href.split('?')[0],
            pageUrlBaseForAnchor: a =>
              a.type === 'creative'
                ? creativeWizardUrl(businessID, a.id)
                : a.type === 'audience'
                ? audiencesUrl(businessID)
                : undefined,
            slackChannelId: business?.slackChannelId,
          }}
        />
      </PresentNavLockContext.Provider>
    </UnresolvedCommentsProvider>
  )
}
